import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Loader2, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function AgencyAcceptInvite() {
    const { token } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [accepting, setAccepting] = useState(false);

    useEffect(() => {
        if (!user) {
            // Redirect to login, preserving the invite link
            navigate(`/login?next=/agency/invite/${token}`);
        }
    }, [user, token, navigate]);

    const accept = async () => {
        setAccepting(true);
        try {
            const { data } = await api.post(`/agencies/invites/${token}/accept`);
            toast.success(`Welcome to ${data.agency.name}!`);
            navigate("/agency");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not accept invite");
        } finally {
            setAccepting(false);
        }
    };

    if (!user) {
        return (
            <div className="min-h-[60vh] grid place-items-center">
                <Loader2 className="h-8 w-8 animate-spin text-coral" />
            </div>
        );
    }

    return (
        <div data-testid="agency-accept-page" className="min-h-[60vh] grid place-items-center px-4 py-12">
            <Card className="max-w-md w-full p-6 lg:p-8 bg-card text-center">
                <Building2 className="h-12 w-12 text-coral mx-auto mb-3" />
                <h1 className="font-display text-2xl font-bold text-foreground mb-2">Join an agency</h1>
                <p className="font-app text-sm text-muted-foreground mb-6">
                    You've been invited to join an agency on Bula Real Estate Fiji. Your listings + JVs will be
                    grouped under the agency banner. Your individual profile and stats remain yours.
                </p>
                <Button
                    onClick={accept}
                    disabled={accepting}
                    data-testid="accept-agency-invite-btn"
                    className="w-full bg-coral hover:bg-coral-deep text-white font-app"
                >
                    {accepting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Accept invite <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
            </Card>
        </div>
    );
}
